import { Container, Button, FormHelperText, Grid, TextFieldProps, TextField as MuiTextField, Card } from "@mui/material";
import { ReduxState, ResourceComponentPropsWithId, ShowBase, useGetOne, useRefresh, useShowContext, useShowController, useUpdate } from "ra-core"
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Link as RouteLink } from 'react-router-dom'
import { useDispatch, useSelector } from "react-redux"
import { Content } from "~/components/content/Content"
import { SaveActionSlim } from "~/dataProvider/types"
import { OdResourcePageProps } from "~/types/resource"
import chainOptions from "~/utils/chainOptions"
import { useFormik } from "formik";
import * as Yup from 'yup'
import OdForm, { OnSubmit } from "~/components/formik/OdForm";
import TextField from "~/components/formik/TextField";
import { decodeHashid } from "~/utils/hashid";
import { NetworkConnectionResponseDto, NetworkGatewayResponseDto, UpdateNetworkConnectionInput } from "~/gen/api/deploy";
import { RecPatch, SupportedResources } from "~/dataProvider/tools";
import { useSnackbar } from "notistack";
import retrieveAxiosErr from "~/utils/retrieveAxiosErr";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LinkIcon from '@mui/icons-material/Link';
import { useResourceRegister } from "~/utils/resourceRegister";
import { getStashedDeployInfo } from "~/reducers/deployStash";
import { useRevealPass } from "~/utils/useRevealPass";



const connectionResource: SupportedResources = 'network-connections'
const gatewayResource: SupportedResources = 'settings/deployment/network-configuration'

const NetworkConnectionDetail: FC<ResourceComponentPropsWithId> = (props) => {
  useResourceRegister({ name: 'network-connection-sku' })
  useResourceRegister({ name: 'random-key' })

  const { id: hId, resource, ...rest } = props
  const id = decodeHashid(hId || '')


  return (
    <ShowBase id={id} resource={connectionResource} {...rest} mutationMode={'pessimistic'} >
      <NetworkConnectionDetailCater id={id as any} {...rest} />
    </ShowBase>
  )
}

const NetworkConnectionDetailCater: FC<ResourceComponentPropsWithId> = (props) => {
  const { id } = props
  const [save, { loading }] = useUpdate<UpdateNetworkConnectionInput & RecPatch>(connectionResource, id)
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const refresh = useRefresh()

  const performSave = useCallback<SaveActionSlim<UpdateNetworkConnectionInput>>((record, callbacks) => {
    save && save(connectionResource, id, { ...record, networkConnectionId: +(id || 0) }, null, chainOptions(callbacks, {
      onSuccess: () => {
        enqueueSnackbar('Update succeed', { variant: 'success' })
        refresh()
      },
      onFailure: (err) => {
        const errMsg = retrieveAxiosErr(err)
        enqueueSnackbar(`Update failed: ${errMsg}`, { variant: 'error' })
      },
    }))
  }, [save, dispatch])

  const keyResourceReady = useSelector((store:ReduxState) => !!store.admin.resources['random-key'])
  if (!keyResourceReady) return null
  return (
    <NetworkConnectionDetailBody save={performSave} {...props} />
  )
}

export interface NetworkConnectionDetailBodyProps extends OdResourcePageProps {
  save: SaveActionSlim<UpdateNetworkConnectionInput>;
}

const validationSchema =
  Yup.object().shape({
    localPublicIp: Yup.string().max(255).required('Local Gateway Public IP is required'),
    localAddressSpaces: Yup.string().max(255).required('Local Address Spaces is required')
      .matches(/^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])(\/(3[0-2]|[1-2][0-9]|[1-9]))$/, 
        'Invalid address space, please input CIDR notation'),
  })

const NetworkConnectionDetailBody: FC<NetworkConnectionDetailBodyProps> = ({
  save,
  options,
}) => {

  const { record } = useShowContext<NetworkConnectionResponseDto & RecPatch>()
  const { record: currGateway } = useShowController<NetworkGatewayResponseDto & RecPatch>({ 
    resource: record ? gatewayResource : 'noop', 
    basePath: '', 
    id: record?.networkGatewayId 
  })

  const initialValues = {
    submit: '',
    localPublicIp: record?.localPublicIp || '',
    localAddressSpaces: record?.localAddressSpaces || '',
  }

  const recordRef = useRef<boolean | null>(!!record)

  const [keyId, setKeyId] = useState((new Date()).getTime())
  const { data: randomKey } = useGetOne<{id:string,key:string}>('random-key', keyId)

  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    const options = {
      onSuccess: () => {
        setStatus({ success: true })
        setSubmitting(false)
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }

    const input: Partial<UpdateNetworkConnectionInput> = { 
      networkConnectionId: record?.networkConnectionId,
      localPublicIp: values.localPublicIp, 
      localAddressSpaces: [values.localAddressSpaces],
      sharedKey: record?.sharedKey || '',
    }
    save(input, options)
  }, [save])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { errors, touched, dirty, isSubmitting, values, handleChange, setFieldValue, setValues, submitForm } = formikCtrl

  useEffect(() => {
    if (!recordRef.current && !!record) {
      recordRef.current = true
      setValues({
        localPublicIp: record.localPublicIp || '',
        localAddressSpaces: (record.localAddressSpaces || [])[0] || '',
      } as any)
    }
  }, [record, setValues])

  const dispatch = useDispatch()

  const { showPass, endAdornment } = useRevealPass()

  const stashedDeployInfo = useSelector(getStashedDeployInfo)

  const regenerateShareKey = () => {
    save({ 
      networkConnectionId: record?.networkConnectionId,
      localPublicIp: record?.localPublicIp || '', 
      localAddressSpaces: record?.localAddressSpaces ? [record?.localAddressSpaces] : [],
      sharedKey: randomKey?.key || '',
    }, {
      onSuccess: () => { setKeyId((new Date()).getTime()) }
    })
  }

  return (
    <Content 
      pageName="Connection Detail" 
      resourceName="Detail" 
      breadcrumbs={[...(options?.breadcrumbs || []), { label: 'Network Configuration', path: `/${gatewayResource || ''}` }]}
      mainActions={
        <>
          <Button
            color="primary"
            disabled={isSubmitting}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            component={RouteLink}
            to={`/${gatewayResource}`}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={isSubmitting}
            type="submit"
            variant="contained"
            // startIcon={<DeleteForeverIcon />}
            onClick={regenerateShareKey}
            sx={{ ml: 4 }}
          >
            Regenerate Shared Key
          </Button>
          {(touched.localAddressSpaces || touched.localPublicIp) &&
            <Button
              color="primary"
              disabled={isSubmitting}
              type="submit"
              variant="contained"
              onClick={submitForm}
              sx={{ ml: 4 }}
            >
              Save
            </Button>}
        </>
      }
      secondaryActions={
        stashedDeployInfo &&
        <Button
          color="primary"
          startIcon={<LinkIcon fontSize="small" />}
          component={RouteLink}
          to={{
            pathname: '/deployments/list',
            state: { openCreate: true }
          }}
        >
          Backup to Deploy Database
        </Button>
      }
    >

      <Card>
        <Container maxWidth="lg" sx={{ pb: 8 }}>
          <Container maxWidth="md">

            <OdForm formikCtrl={formikCtrl}>

              <Grid
                container
                spacing={4}
                sx={{ mt: 4 }}
              >
                <Grid item xs={12} >
                  <ReadonlyField id="readonly-name" label="Connection Name" name="name" value={record?.name || ''} disabled />
                </Grid>
                <Grid item xs={12} >
                  <ReadonlyField id="readonly-gv" label="VPN Gateway" name="gatewayname" value={currGateway?.name || ''} disabled />
                </Grid>
                <Grid item xs={12} >
                  <ReadonlyField id="readonly-endpoint" label="OnData Gateway Endpoint" name="gatewayEndpoint" value={currGateway?.onDataPublicIp || ''} disabled />
                </Grid>
                <Grid item xs={12} >
                  <TextField label="Local Gateway Public IP*" name="localPublicIp" />
                </Grid>
                <Grid item xs={12} >
                  <TextField label="Local Address Spaces*" name="localAddressSpaces" />
                </Grid>
                <Grid item xs={12} >
                  <ReadonlyField id="readonly-sharedKey" 
                    label="Shared Key" 
                    name="sharedKey" 
                    value={record?.sharedKey || ''} 
                    type={showPass ? 'text' : 'password'} 
                    InputProps={{ endAdornment }}
                  />
                </Grid>
                
                {errors.submit && (
                  <Grid item xs={12} >
                    <FormHelperText error>
                      {errors.submit}
                    </FormHelperText>
                  </Grid>
                )}
              </Grid>

            </OdForm>
          </Container>
        </Container>
      </Card>

    </Content>
  )
}

export default NetworkConnectionDetail;


const ReadonlyField: FC<TextFieldProps> = (props) => {
  return (
    <MuiTextField
      fullWidth
      variant="outlined"
      InputProps={{
        readOnly: true,
      }}
      {...props}
    />
  )
}
